/**
 * Hooks for per-job readiness and the competitiveness average (flag `emt_readiness_v3`).
 * The rules live in src/lib/readiness/jobReadiness.ts; this file only loads the board's jobs and
 * re-runs the rules whenever the profile or the jobs change. Nothing is stored.
 */
import { useEffect, useMemo, useState } from 'react';
import type { Job } from '../../lib/firestore/types';
import { listOpenJobsPaginated } from '../../lib/firestore/jobs';
import { calculateReadiness } from '../../lib/readiness/calculateReadiness';
import { toReadinessProfile } from '../../lib/readiness/profileAdapter';
import { evaluateJobForCandidate } from '../../lib/jobs/candidateJobEvaluation';
import { competitivenessFromJobs, type CompetitivenessView } from '../../lib/readiness/jobReadiness';
import { DRIVER_ROLES_FLAG, isNonEmsProfile } from '../../lib/jobs/driverRoles';
import { useFeatureFlag } from '../../context/FeatureFlagsContext';
import { useAuthCtx } from '../../context/AuthContext';
import type { LatLng } from '../../lib/jobs/jobPreferenceMatching';

/** Every open job on the board (same query and exclusions as the Jobs board), up to this many. */
const AVERAGE_JOB_LIMIT = 300;
const CACHE_MS = 60_000;
let cache: { at: number; promise: Promise<Job[]> } | null = null;

/** The board's open jobs, shared by every surface for a minute so the average is not loaded twice. */
export function loadBoardJobsForAverage(force = false): Promise<Job[]> {
  if (!force && cache && Date.now() - cache.at < CACHE_MS) return cache.promise;
  const promise = listOpenJobsPaginated(AVERAGE_JOB_LIMIT).then((page) => page.jobs);
  cache = { at: Date.now(), promise };
  promise.catch(() => {
    cache = null;
  });
  return promise;
}

export function emtCoordsOf(profile: unknown): LatLng | null {
  const p = (profile ?? {}) as { address?: { lat?: unknown; lng?: unknown }; geo?: { lat?: unknown; lng?: unknown } };
  if (typeof p.address?.lat === 'number' && typeof p.address?.lng === 'number') return { lat: p.address.lat, lng: p.address.lng };
  if (typeof p.geo?.lat === 'number' && typeof p.geo?.lng === 'number') return { lat: p.geo.lat, lng: p.geo.lng };
  return null;
}

export type CompetitivenessState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; view: CompetitivenessView; jobs: Job[] };

/** The overall competitiveness for this EMT, recomputed whenever the profile or the job list changes. */
export function useCompetitiveness(profile: unknown, enabled = true): { state: CompetitivenessState; retry: () => void } {
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const driverRoles = useFeatureFlag(DRIVER_ROLES_FLAG);
  const { user } = useAuthCtx();
  // The signed-in account, not profile.uid: the profile object is the Firestore document's data, and a
  // profile written without a `uid` field (the EMT sign-up never writes one) left this on loading forever.
  const uid = user?.uid || (profile as { uid?: string } | null)?.uid;
  const hasProfile = Boolean(profile);

  useEffect(() => {
    if (!enabled || !uid || !hasProfile) return;
    let active = true;
    setFailed(false);
    loadBoardJobsForAverage(attempt > 0)
      .then((list) => active && setJobs(list))
      .catch((err) => {
        console.warn('[readiness] could not load jobs for the average', err);
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [enabled, uid, hasProfile, attempt]);

  const state = useMemo<CompetitivenessState>(() => {
    if (failed) return { status: 'error' };
    if (!jobs || !profile) return { status: 'loading' };
    const readinessProfile = toReadinessProfile(profile as Parameters<typeof toReadinessProfile>[0]);
    const readiness = readinessProfile ? calculateReadiness(readinessProfile) : null;
    const options = { driverRoles };
    const p = profile as { address?: { state?: string }; professional?: { jobPreferences?: { maxTravelRadiusMiles?: number | null } } };
    const view = competitivenessFromJobs({
      jobs,
      profile,
      readinessProfile,
      scoreFor: (job) => evaluateJobForCandidate({ profile, readinessProfile, readiness, job, options }).match?.total ?? null,
      emtState: p.address?.state ?? null,
      emtCoords: emtCoordsOf(profile),
      maxTravelRadiusMiles: p.professional?.jobPreferences?.maxTravelRadiusMiles ?? null,
      matchOptions: options,
      farOffOptions: { driverRoles, driver: isNonEmsProfile(profile, driverRoles) },
    });
    return { status: 'ready', view, jobs };
  }, [jobs, failed, profile, driverRoles]);
  return { state, retry: () => setAttempt((n) => n + 1) };
}
